import type { Condition as FHIRCondition } from '@medplum/fhirtypes';

/**
 * Condition view model with a simplified interface for UI consumption
 */
export interface Condition {
  /** Condition ID from FHIR resource */
  id: string;
  /** Display name of the condition */
  display: string;
  /** Clinical status (active, recurrence, relapse, inactive, remission, resolved) */
  clinicalStatus?: string;
  /** Verification status (unconfirmed, provisional, differential, confirmed, etc.) */
  verificationStatus?: string;
  /** Severity (mild, moderate, severe) */
  severity?: string;
  /** Date of onset */
  onsetDate?: string;
  /** Date of abatement (resolution) */
  abatementDate?: string;
  /** ICD-10 or other coding system codes */
  codes?: Array<{
    system?: string;
    code?: string;
    display?: string;
  }>;
  /** Notes from FHIR Annotation array */
  notes?: string;
  /** Original FHIR resource for advanced use cases */
  _original: FHIRCondition;
}

function extractDisplay(fhirCondition: FHIRCondition): string {
  const code = fhirCondition.code;
  return code?.text || code?.coding?.[0]?.display || 'Unknown Condition';
}

function extractCodes(fhirCondition: FHIRCondition): Condition['codes'] {
  const code = fhirCondition.code;
  if (!code?.coding) return [];

  return code.coding.map(c => ({
    system: c.system,
    code: c.code,
    display: c.display,
  }));
}

/**
 * Extract notes from FHIR Condition
 */
function extractNotes(fhirCondition: FHIRCondition): string | undefined {
  const notes = fhirCondition.note;
  if (!notes || notes.length === 0) return undefined;
  return notes.map(n => n.text).join('\n');
}

/**
 * Convert a FHIR Condition resource to a simplified Condition view model
 */
export function fhirConditionToViewModel(fhirCondition: FHIRCondition): Condition {
  const clinicalStatus = fhirCondition.clinicalStatus?.coding?.[0]?.code;
  const verificationStatus = fhirCondition.verificationStatus?.coding?.[0]?.code;
  const severity = fhirCondition.severity?.text || fhirCondition.severity?.coding?.[0]?.display;

  return {
    id: fhirCondition.id || 'unknown',
    display: extractDisplay(fhirCondition),
    clinicalStatus,
    verificationStatus,
    severity,
    onsetDate: fhirCondition.onsetDateTime,
    abatementDate: fhirCondition.abatementDateTime,
    codes: extractCodes(fhirCondition),
    notes: extractNotes(fhirCondition),
    _original: fhirCondition,
  };
}
